import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  Reducer,
  createSelector,
} from '@reduxjs/toolkit'
import cuid from 'cuid'
import api from '@disco/api/server'
import { isShortenedUrl, getDiscoShortenedUrl } from '@disco/utils/hostnames'
import { DEFAULT_SOURCE_SCOPE } from '@disco/utils/search-page'
import { Track, TrackId } from '@disco/types/types/track'
import { Group } from '@disco/types/types/group'
import type { SourceScope } from '@disco/types/types/source-scope'
import type { SearchPageState, SearchType, LinkInfo } from 'types/search-page'
import type {
  RowSelectionState,
  VisibilityState,
  ColumnOrderState,
  ColumnSizingState,
  ColumnPinningState,
  SortingState,
} from 'types/data-table'
import type { FilterState } from 'types/filter-page'
import { State } from '.'

import { TRACKS_UPDATE } from '@disco/constants/ActionTypes'
import {
  defaultVisibleColumnIds,
  DEFAULT_SEGMENT_START,
  DEFAULT_SEGMENT_END,
} from '@disco/constants/search'
import {
  AIMS,
  NEW_SEARCH_MDS_ENABLE,
  UNIFIED_SEARCH_ENABLE,
} from '@disco/constants/FeatureTypes'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { normalizeV2Track, buildV2SearchQuery } from '@disco/utils/search-page'
import { getMusicProviderFromUrl } from '@disco/utils/links'
import { getConstanceSetting } from '@disco/state-management/selectors/user'
import { pushUINotification } from '@disco/utils/notifications'

export const selectIsMdsSearchEnabled = (state: State): boolean =>
  Boolean(getConstanceSetting(state, NEW_SEARCH_MDS_ENABLE))

export const selectIsUnifiedSearchEnabled = (state: State): boolean =>
  Boolean(getConstanceSetting(state, UNIFIED_SEARCH_ENABLE))

const notifyUnsupportedSourceFilter = (): void => {
  pushUINotification({
    level: 'warning',
    message:
      'The selected source filter is not supported when MDS search is enabled. Showing results from all sources.',
    dismissible: 'click',
  })
}

const withFilterIds = (filters: FilterState[]): FilterState[] =>
  filters.map(filter => (filter.id ? filter : { ...filter, id: cuid() }))

/**
 * Resolves the stable key used to track a column's visibility.
 *
 * Precedence: explicit `id` > `meta.metadataDef.field` > string `header`.
 * The metadata field wins over `header` because dynamic metadata columns
 * share a generic header label (e.g. the localised field name) while their
 * `field` is the unique, stable identifier we persist visibility against.
 * Falling back to `header` last keeps static columns that only set a string
 * header working.
 */
const getDefaultColumnVisibilityKey = (column: {
  id?: string
  header?: unknown
  meta?: { metadataDef?: { field?: string } }
}): string | undefined => {
  if (column.id) {
    return column.id
  }

  if (column.meta?.metadataDef?.field) {
    return column.meta.metadataDef.field
  }

  if (typeof column.header === 'string') {
    return column.header
  }

  return undefined
}

// Dispatch TRACKS_UPDATE with any tracks
const dispatchTracksUpdate = (results: supportedTypes[], dispatch: any) => {
  const tracks = results
    .filter((item): item is Track => (item as Track).type === 'track')
    .map(normalizeV2Track) // Normalize V2 tracks to expected format

  if (tracks.length > 0) {
    dispatch({ type: TRACKS_UPDATE, tracks })
  }
}

type supportedTypes = Track | Group

// Transform segment values to API format (rounded integers, start clamped to >= 1)
const toApiSegmentStart = (start: number): number =>
  Math.max(1, Math.round(start))
const toApiSegmentEnd = (end: number): number => Math.round(end)

const initialState: SearchPageState = {
  results: [],
  cursor: null,
  hasMore: false,
  query: '',
  searchType: 'keyword',
  sorting: [],
  filters: [],
  sourceScope: [...DEFAULT_SOURCE_SCOPE],
  rowSelection: {},
  columnVisibility: {},
  columnOrder: [],
  columnSizing: {},
  columnPinning: {
    left: ['select', 'Title'],
    right: ['actions'],
  },
  initialFetchLoaded: false,
  isLoading: false,
  isLoadingNextPage: false,
  error: null,
  context: null,
  linkInfo: null,
  // Segment selection defaults
  segmentStart: DEFAULT_SEGMENT_START,
  segmentEnd: DEFAULT_SEGMENT_END,
  appliedSegmentStart: null,
  appliedSegmentEnd: null,
  segmentAppliedByUser: false,
}

// Cache for link info to avoid refetching the same URLs
const linkInfoCache: Record<string, LinkInfo> = {}

// Thunk to fetch link info
export const fetchLinkInfo = createAsyncThunk<
  LinkInfo | null,
  string,
  { state: State; rejectValue: string }
>('searchPage/fetchLinkInfo', async (url, { rejectWithValue }) => {
  // Check cache first
  if (linkInfoCache[url]) {
    return linkInfoCache[url]
  }

  // Determine provider from URL using shared utility
  const provider = getMusicProviderFromUrl(url)
  let queryUrl: string = url

  if (!provider || !queryUrl) {
    return null
  }
  // For disco links, transform the URL format if needed
  const isDiscoUrlShortened = provider === 'disco' && isShortenedUrl(url)

  if (provider === 'disco' && !isDiscoUrlShortened) {
    try {
      const shortenedUrl = await getDiscoShortenedUrl(url)
      if (shortenedUrl.success) {
        queryUrl = shortenedUrl.url
      } else {
        return null
      }
    } catch (error: unknown) {
      console.error('Error shortening disco URL:', error)
      return null
    }
  }

  try {
    const response = await api.getLinkInfo(queryUrl)
    const linkInfo: LinkInfo = {
      ...response,
      provider,
    }

    // Cache the result
    linkInfoCache[url] = linkInfo

    return linkInfo
  } catch (error: unknown) {
    // 400 Bad Request means unsupported URL - don't show preview
    const status =
      typeof error === 'object' && error !== null && 'status' in error
        ? (error as { status?: number }).status
        : undefined
    if (status === 400) {
      return null
    }
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch link info',
    )
  }
})

let abortController: AbortController

// V2 Search Thunk
export const fetchResults = createAsyncThunk<
  {
    hits: supportedTypes[]
    pagination: { cursor: string | null; has_more: boolean }
    context?: { title: string; waveform_url: string }
  },
  void,
  { state: State; dispatch: any; rejectValue: string }
>(
  'searchPage/fetchResults',
  async (_, { getState, dispatch, rejectWithValue }) => {
    if (abortController) abortController.abort()
    abortController = new AbortController()

    const state = getState()
    const searchPageState = state.searchPage
    const mdsSearchEnabled = selectIsMdsSearchEnabled(state)
    const aimsEnabled = isFeatureEnabled(state, { featureName: AIMS })

    const v2Query = buildV2SearchQuery(
      searchPageState,
      undefined,
      mdsSearchEnabled,
      notifyUnsupportedSourceFilter,
      aimsEnabled,
    )

    try {
      const response = await api.searchV2(v2Query, abortController.signal)

      dispatchTracksUpdate(response.hits, dispatch)

      return {
        hits: response.hits,
        pagination: response.pagination,
        ...(response.context && { context: response.context }),
      }
    } catch (error: unknown) {
      // Extract error message from response body if available
      const body =
        typeof error === 'object' && error !== null && 'body' in error
          ? (error as { body?: { error?: string } }).body
          : undefined
      const errorMessage =
        body?.error ||
        (error instanceof Error ? error.message : undefined) ||
        'Failed to fetch results'
      return rejectWithValue(errorMessage)
    }
  },
)

// V2 Next Page Thunk
export const fetchNextPage = createAsyncThunk<
  {
    hits: supportedTypes[]
    pagination: { cursor: string | null; has_more: boolean }
    context?: { title: string; waveform_url: string }
  },
  void,
  { state: State; dispatch: any }
>('searchPage/fetchNextPage', async (_, { getState, dispatch }) => {
  const state = getState()
  const searchPageState = state.searchPage

  if (!searchPageState.cursor) {
    return { hits: [], pagination: { cursor: null, has_more: false } }
  }

  const mdsSearchEnabled = selectIsMdsSearchEnabled(state)
  const aimsEnabled = isFeatureEnabled(state, { featureName: AIMS })

  // Build V2 query with cursor for pagination
  const v2Query = buildV2SearchQuery(
    searchPageState,
    searchPageState.cursor,
    mdsSearchEnabled,
    notifyUnsupportedSourceFilter,
    aimsEnabled,
  )

  const response = await api.searchV2(v2Query)

  dispatchTracksUpdate(response.hits, dispatch)

  return {
    hits: response.hits,
    pagination: response.pagination,
    ...(response.context && { context: response.context }),
  }
})

const searchPageSlice = createSlice({
  name: 'searchPage',
  initialState,
  reducers: {
    setQuery(state, action: PayloadAction<string>) {
      state.query = action.payload
    },
    setSearchType(state, action: PayloadAction<SearchType>) {
      state.searchType = action.payload
      // Clear context and link info when changing search type
      state.context = null
      state.linkInfo = null
    },
    clearLinkInfo(state) {
      state.linkInfo = null
    },
    setFilters: {
      reducer(state, action: PayloadAction<FilterState[]>) {
        state.filters = action.payload
      },
      prepare(filters: FilterState[]) {
        return { payload: withFilterIds(filters) }
      },
    },
    clearSearch: {
      reducer(state, action: PayloadAction<FilterState[]>) {
        state.query = ''
        state.filters = action.payload
        state.sourceScope = [...DEFAULT_SOURCE_SCOPE]
      },
      prepare(filters: FilterState[]) {
        return { payload: withFilterIds(filters) }
      },
    },
    clearResults(state) {
      state.results = []
      state.cursor = null
      state.hasMore = false
      state.context = null
    },
    setSourceScope(
      state,
      action: PayloadAction<[SourceScope, ...SourceScope[]]>,
    ) {
      state.sourceScope = action.payload
    },
    resetSourceScope(state) {
      state.sourceScope = [...DEFAULT_SOURCE_SCOPE]
    },
    setSorting(state, action: PayloadAction<SortingState>) {
      state.sorting = action.payload
    },
    setRowSelection(state, action: PayloadAction<RowSelectionState>) {
      state.rowSelection = action.payload
    },
    setColumnVisibility(state, action: PayloadAction<VisibilityState>) {
      state.columnVisibility = action.payload
    },
    setColumnOrder(state, action: PayloadAction<ColumnOrderState>) {
      state.columnOrder = action.payload
    },
    setColumnSizing(state, action: PayloadAction<ColumnSizingState>) {
      state.columnSizing = action.payload
    },
    setColumnPinning(state, action: PayloadAction<ColumnPinningState>) {
      state.columnPinning = action.payload
    },
    initializeDefaultColumnVisibility(
      state,
      action: PayloadAction<{
        columns: ReadonlyArray<{
          id?: string
          header?: unknown
          meta?: { metadataDef?: { field?: string } }
        }>
      }>,
    ) {
      if (Object.keys(state.columnVisibility).length === 0) {
        const defaultVisibility = action.payload.columns.reduce(
          (acc, column) => {
            const key = getDefaultColumnVisibilityKey(column)
            if (key) {
              acc[key] = defaultVisibleColumnIds.includes(key)
            }
            return acc
          },
          {} as Record<string, boolean>,
        )

        state.columnVisibility = defaultVisibility
      }
    },
    setSegment(
      state,
      action: PayloadAction<{ start: number; end: number; duration?: number }>,
    ) {
      const { start, end, duration } = action.payload
      // Validate: start must be >= 0
      const validStart = Math.max(0, start)
      // Validate: end must be > start and <= duration (if provided)
      // Ensure cap is at least validStart + 1 to prevent end < start
      const cap =
        duration !== undefined ? Math.max(duration, validStart + 1) : undefined
      const validEnd =
        cap !== undefined
          ? Math.min(Math.max(validStart + 1, end), cap)
          : Math.max(validStart + 1, end)
      state.segmentStart = validStart
      state.segmentEnd = validEnd
    },
    applySegment(state) {
      // Copy current segment position to applied segment
      // Uses toApiSegmentStart/End for consistent transformation
      state.appliedSegmentStart = toApiSegmentStart(state.segmentStart)
      state.appliedSegmentEnd = toApiSegmentEnd(state.segmentEnd)
      state.segmentAppliedByUser = true
    },
    deactivateAppliedSegment(state) {
      // Clears "applied by user" only — preserves appliedSegmentStart/End so selectShowApplyButton still works after a waveform seek.
      state.segmentAppliedByUser = false
    },
    setAppliedSegment(
      state,
      action: PayloadAction<{ start: number | null; end: number | null }>,
    ) {
      state.appliedSegmentStart = action.payload.start
      state.appliedSegmentEnd = action.payload.end
    },
    prepareSimSearch(state, action: PayloadAction<string>) {
      state.query = action.payload
      state.searchType = 'similarity'
      state.initialFetchLoaded = false
      state.context = null
      state.linkInfo = null
      state.error = null
      state.results = []
    },
    resetSegment(state, action: PayloadAction<{ duration?: number }>) {
      // Reset segment to default position based on track duration
      // Rules:
      // - Track < 60s: use full track (0 to duration)
      // - Track 60-89s: 60s segment starting at 0 (0 to 60)
      // - Track >= 90s: 60s segment starting at 30 (30 to 90)
      const duration = action.payload.duration
      let newStart: number
      let newEnd: number

      if (duration !== undefined) {
        if (duration < 60) {
          // Short track: use full track
          newStart = 0
          newEnd = duration
        } else if (duration < 90) {
          // Medium track: 60s segment starting at 0
          newStart = 0
          newEnd = 60
        } else {
          // Long track: 60s segment starting at 30
          newStart = DEFAULT_SEGMENT_START
          newEnd = DEFAULT_SEGMENT_END
        }
      } else {
        // No duration provided: use default (30-90)
        newStart = DEFAULT_SEGMENT_START
        newEnd = DEFAULT_SEGMENT_END
      }

      state.segmentStart = newStart
      state.segmentEnd = newEnd
      // Set applied to match initial position so drag handle shows by default
      // Any subsequent drag will make them differ, showing the Apply button
      state.appliedSegmentStart = newStart
      state.appliedSegmentEnd = newEnd
      state.segmentAppliedByUser = false
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchResults.pending, state => {
        state.isLoading = true
        state.error = null
        // Keep context during search to maintain embed visibility
        // state.context = null  // Commented out to prevent embed disappearing
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        state.isLoading = false
        state.results = action.payload.hits.map((item: any) => item.id)
        state.cursor = action.payload.pagination.cursor
        state.hasMore = action.payload.pagination.has_more
        state.context = action.payload.context || null
        state.initialFetchLoaded = true
      })
      .addCase(fetchResults.rejected, (state, action) => {
        state.isLoading = false
        state.error =
          action.payload || action.error.message || 'Failed to fetch results'
      })
      .addCase(fetchNextPage.pending, state => {
        state.isLoadingNextPage = true
        state.error = null
      })
      .addCase(fetchNextPage.fulfilled, (state, action) => {
        state.isLoadingNextPage = false
        state.results = [
          ...state.results,
          ...action.payload.hits.map((item: any) => item.id),
        ]
        state.cursor = action.payload.pagination.cursor
        state.hasMore = action.payload.pagination.has_more
        // Only set context from pagination if not already established by the initial fetch.
        // Overwriting would change waveform_url mid-playback and remount SimilarityPlayer.
        if (action.payload.context && !state.context) {
          state.context = action.payload.context
        }
      })
      .addCase(fetchNextPage.rejected, (state, action) => {
        state.isLoadingNextPage = false
        state.error = action.error.message || 'Failed to fetch next page'
      })
      .addCase(fetchLinkInfo.fulfilled, (state, action) => {
        state.linkInfo = action.payload
      })
      .addCase(fetchLinkInfo.rejected, state => {
        // On error, just don't show the link preview
        state.linkInfo = null
      })
  },
})

export const {
  setQuery,
  setSearchType,
  setFilters,
  clearSearch,
  setSourceScope,
  resetSourceScope,
  setSorting,
  setRowSelection,
  setColumnVisibility,
  setColumnPinning,
  setColumnOrder,
  setColumnSizing,
  initializeDefaultColumnVisibility,
  clearLinkInfo,
  setSegment,
  applySegment,
  deactivateAppliedSegment,
  setAppliedSegment,
  resetSegment,
  prepareSimSearch,
} = searchPageSlice.actions

// Combined thunk to apply segment and fetch results atomically
// Ensures applySegment runs before fetchResults reads the applied values
// Rolls back applied segment if fetch fails so Apply button reappears
export const applySegmentAndSearch = createAsyncThunk<
  void,
  void,
  { state: State; dispatch: any }
>('searchPage/applySegmentAndSearch', async (_, { getState, dispatch }) => {
  // Capture previous applied values for potential rollback
  const { appliedSegmentStart, appliedSegmentEnd } = getState().searchPage

  dispatch(applySegment())

  const result = await dispatch(fetchResults())

  // If fetch failed, restore previous applied values so Apply button reappears
  if (fetchResults.rejected.match(result)) {
    dispatch(
      setAppliedSegment({
        start: appliedSegmentStart,
        end: appliedSegmentEnd,
      }),
    )
  }
})

export const clearSearchAndFetch = createAsyncThunk<void, FilterState[]>(
  'searchPage/clearSearchAndFetch',
  async (value, { dispatch }) => {
    dispatch(clearSearch(value))
    try {
      await dispatch(fetchResults()).unwrap()
    } catch (error: unknown) {
      dispatch(searchPageSlice.actions.clearResults())
      let message: string
      if (typeof error === 'string') {
        message = error
      } else {
        console.error('Failed to fetch results after clearing search:', error)
        message = 'Failed to fetch results'
      }
      pushUINotification({
        level: 'error',
        message,
        dismissible: 'click',
      })
    }
  },
)

export const selectSearchPage = (state: State) =>
  state.searchPage as SearchPageState

export const selectSearchPageResultIds = (state: State): TrackId[] =>
  state.searchPage.results

export const selectSearchPageResults = createSelector(
  [selectSearchPageResultIds, (state: State) => state.tracks],
  (resultIds: TrackId[], tracks: Record<TrackId, Track>) =>
    resultIds.map(id => tracks[id]).filter(Boolean),
)

// Segment selection selectors
export const selectSegmentStart = (state: State): number =>
  state.searchPage.segmentStart

export const selectSegmentEnd = (state: State): number =>
  state.searchPage.segmentEnd

export const selectAppliedSegmentStart = (state: State): number | null =>
  state.searchPage.appliedSegmentStart

export const selectAppliedSegmentEnd = (state: State): number | null =>
  state.searchPage.appliedSegmentEnd

export const selectHasAppliedSegment = (state: State): boolean =>
  state.searchPage.appliedSegmentStart !== null &&
  state.searchPage.appliedSegmentEnd !== null

export const selectSearchPageIsLoading = (state: State): boolean =>
  state.searchPage.isLoading

// True only when user explicitly applied a segment and the current position still matches.
export const selectIsSegmentApplied = (state: State): boolean => {
  const { segmentStart, appliedSegmentStart, segmentAppliedByUser } =
    state.searchPage
  return (
    segmentAppliedByUser &&
    appliedSegmentStart !== null &&
    toApiSegmentStart(segmentStart) === appliedSegmentStart
  )
}

// Show Apply button when segment has been moved from its applied position
// Uses same transformation as applySegment for consistent comparison
export const selectShowApplyButton = (state: State): boolean => {
  const { segmentStart, segmentEnd, appliedSegmentStart, appliedSegmentEnd } =
    state.searchPage
  if (appliedSegmentStart === null || appliedSegmentEnd === null) {
    return false
  }
  return (
    toApiSegmentStart(segmentStart) !== appliedSegmentStart ||
    toApiSegmentEnd(segmentEnd) !== appliedSegmentEnd
  )
}

export { SearchPageState }
export default searchPageSlice.reducer as Reducer<SearchPageState>
